import { useQuery } from '@tanstack/react-query';
import { subscriptionApi } from 'shared/services/planApi';
import { queryKeys } from '../queryConfig';

/**
 * Whether a company's current seat usage exceeds its plan's effective cap.
 * Used to surface a persistent over-capacity warning to global roles
 * browsing the company and to the company's own company_admin.
 *
 * Pass a falsy `companyId` (e.g. for platform users not browsing a company)
 * to disable the query — it resolves to a non-over-capacity default.
 */
export function useCompanyPlanStatus(companyId?: string | null) {
  const query = useQuery({
    queryKey: queryKeys.subscriptions.seatUsage(companyId || ''),
    queryFn: () => subscriptionApi.getSeatUsage(companyId as string),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });

  const effectiveCap = query.data?.effectiveCap ?? null;
  const effectiveSeats = query.data?.effectiveSeats ?? 0;

  return {
    seatUsage: query.data,
    // effectiveCap === null means no plan is assigned — not the same as
    // over-capacity, so it's excluded here.
    overCapacity: effectiveCap !== null && effectiveSeats > effectiveCap,
    effectiveSeats,
    effectiveCap,
    loading: query.isLoading,
    refetch: query.refetch,
  };
}
